import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  ISubscriptionContractTypePrices,
  ISubscriptionType,
  Subscription
} from '../../resources/types/balanceTypes';
import { selectCities } from '../../redux/slices/citiesSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { RootState } from '../../redux/store';
import { SelectOption } from '../../components/Select/interface';
import Select from '../../components/Select';
import { useFormik } from 'formik';
import SubscriptionModalPricesGroup from './SubscriptionModalPricesGroup';
import { createOrUpdateSubscriptionAsync } from '../../redux/slices/subscriptionSlice';

const defaultPricesData: ISubscriptionContractTypePrices = {
  start: null,
  normal: null,
  reservation: null,
  pause: null
};

const defaultPricesDurationData: ISubscriptionContractTypePrices = {
  normal: null,
  reservation: null,
  pause: null
};

const defaultPricesFreeActionsData: ISubscriptionContractTypePrices = {
  start: null
};

const defaultLocalized = { en: '', ua: '', de: '', es: '' };

interface ISubscriptionModalProps {
  selectedSubscription: Subscription | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  setSelectedSubscription: (subscription: Subscription) => void;
}

const SubscriptionModal = ({
  open,
  selectedSubscription,
  setOpen,
  setSelectedSubscription
}: ISubscriptionModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const cities = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);

  const typeSelectorOptions: SelectOption[] = [
    {
      label: t('subscriptions.typeFreeUnlocks'),
      value: ISubscriptionType.priceForContractActions
    },
    {
      label: t('subscriptions.typeLimited'),
      value: ISubscriptionType.freeContractActions
    },
    {
      label: t('subscriptions.typeDiscounts'),
      value: ISubscriptionType.discountPercentForContractActions
    }
  ];

  const cityId = useMemo(() => {
    const city = cities.items.find(item => item._id === adminInfo.currentCity);
    if (city) {
      return city._id;
    }
  }, [adminInfo.currentCity, cities.items]);

  const {
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
    values,
    resetForm
  } = useFormik({
    initialValues: {
      name: '',
      description: '',
      nameLocalized: { ...defaultLocalized },
      descriptionLocalized: { ...defaultLocalized },
      duration: 0,
      price: 0,
      city: cityId,
      type: ISubscriptionType.priceForContractActions,
      contractPrices: defaultPricesData,
      freeActions: defaultPricesFreeActionsData,
      freeDurationSeconds: defaultPricesDurationData,
      contractPricesDiscountPercents: defaultPricesData
    },
    onSubmit: values => {
      const {
        name,
        description,
        nameLocalized,
        descriptionLocalized,
        duration,
        price,
        city,
        type,
        contractPrices,
        freeActions,
        freeDurationSeconds,
        contractPricesDiscountPercents
      } = values;
      const subscriptionCity = city ?? (cityId as string);

      dispatch(
        createOrUpdateSubscriptionAsync(
          selectedSubscription?._id ?? '',
          {
            name,
            description,
            nameLocalized,
            descriptionLocalized,
            duration,
            price,
            city: subscriptionCity,
            type,
            ...(values.type === ISubscriptionType.priceForContractActions
              ? { contractPrices }
              : {}),
            ...(values.type === ISubscriptionType.freeContractActions
              ? { freeActions, freeDurationSeconds }
              : {}),
            ...(values.type ===
            ISubscriptionType.discountPercentForContractActions
              ? { contractPricesDiscountPercents }
              : {})
          },
          () => {
            resetForm();
            setOpen(false);
          }
        )
      );
    }
  });

  useEffect(() => {
    console.log('-=-=- useEffect', open, selectedSubscription);
    if (open && selectedSubscription !== null) {
      setValues({
        name: selectedSubscription?.name ?? '',
        description: selectedSubscription?.description ?? '',
        nameLocalized: {
          en: selectedSubscription?.nameLocalized?.en ?? '',
          ua: selectedSubscription?.nameLocalized?.ua ?? '',
          de: selectedSubscription?.nameLocalized?.de ?? '',
          es: selectedSubscription?.nameLocalized?.es ?? '',
        },
        descriptionLocalized: {
          en: selectedSubscription?.descriptionLocalized?.en ?? '',
          ua: selectedSubscription?.descriptionLocalized?.ua ?? '',
          de: selectedSubscription?.descriptionLocalized?.de ?? '',
          es: selectedSubscription?.descriptionLocalized?.es ?? '',
        },
        duration: selectedSubscription?.duration ?? 0,
        price: selectedSubscription?.price ?? 0,
        city: selectedSubscription?.city ?? cityId,
        type:
          selectedSubscription?.type ??
          ISubscriptionType.priceForContractActions,
        contractPrices:
          selectedSubscription?.contractPrices ?? defaultPricesData,
        freeActions:
          selectedSubscription?.freeActions ?? defaultPricesFreeActionsData,
        freeDurationSeconds:
          selectedSubscription?.freeDurationSeconds ??
          defaultPricesDurationData,
        contractPricesDiscountPercents:
          selectedSubscription?.contractPricesDiscountPercents ??
          defaultPricesData
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (values.type === ISubscriptionType.priceForContractActions) {
      setFieldValue('freeActions', defaultPricesFreeActionsData);
      setFieldValue('freeDurationSeconds', defaultPricesDurationData);
      setFieldValue('contractPricesDiscountPercents', defaultPricesData);
    }

    if (values.type === ISubscriptionType.freeContractActions) {
      setFieldValue('contractPrices', defaultPricesData);
      setFieldValue('contractPricesDiscountPercents', defaultPricesData);
    }

    if (values.type === ISubscriptionType.discountPercentForContractActions) {
      setFieldValue('contractPrices', defaultPricesData);
      setFieldValue('freeActions', defaultPricesFreeActionsData);
      setFieldValue('freeDurationSeconds', defaultPricesDurationData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.type]);

  const isSuperAdmin = useSelector(
    (state: RootState) => state.auth.info?.type === 'superAdmin'
  );

  const citiesOptions = useMemo<SelectOption[]>(
    () => [
      {
        label: t('subscriptions.all'),
        value: ''
      },
      ...cities.items.map(city => ({
        label: city.cityName,
        value: city._id
      }))
    ],
    [cities.items]
  );

  return (
    <Dialog fullWidth maxWidth='sm' open={open} onClose={() => setOpen(false)}>
      <>
        <DialogTitle>
          {selectedSubscription?._id
            ? t('subscriptions.editTitle')
            : t('subscriptions.createTitle')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('subscriptions.name')}
            name='name'
            onChange={handleChange}
            value={values.name}
          />

          <InputLabel style={{ fontSize: "13px", marginTop: 16, fontWeight: 600 }}>{t('subscriptions.localizedNames')}</InputLabel>
          <TextField fullWidth label={t('subscriptions.nameEn')} margin='dense' size='small' value={values.nameLocalized.en} onChange={e => setFieldValue('nameLocalized.en', e.target.value)} />
          <TextField fullWidth label={t('subscriptions.nameUa')} margin='dense' size='small' value={values.nameLocalized.ua} onChange={e => setFieldValue('nameLocalized.ua', e.target.value)} />
          <TextField fullWidth label={t('subscriptions.nameDe')} margin='dense' size='small' value={values.nameLocalized.de} onChange={e => setFieldValue('nameLocalized.de', e.target.value)} />
          <TextField fullWidth label={t('subscriptions.nameEs')} margin='dense' size='small' value={values.nameLocalized.es} onChange={e => setFieldValue('nameLocalized.es', e.target.value)} />

          <InputLabel style={{ fontSize: "13px", marginTop: 20}}>{t('subscriptions.description')}</InputLabel>
          <textarea style={{ resize: "none", width: "100%" }} rows={4} onChange={event => setFieldValue('description', event.target.value)} value={values.description}></textarea>

          <InputLabel style={{ fontSize: "13px", marginTop: 16, fontWeight: 600 }}>{t('subscriptions.localizedDescriptions')}</InputLabel>
          <InputLabel style={{ fontSize: "12px", marginTop: 4}}>{t('subscriptions.descEn')}</InputLabel>
          <textarea style={{ resize: "none", width: "100%" }} rows={3} onChange={event => setFieldValue('descriptionLocalized.en', event.target.value)} value={values.descriptionLocalized.en}></textarea>
          <InputLabel style={{ fontSize: "12px", marginTop: 4}}>{t('subscriptions.descUa')}</InputLabel>
          <textarea style={{ resize: "none", width: "100%" }} rows={3} onChange={event => setFieldValue('descriptionLocalized.ua', event.target.value)} value={values.descriptionLocalized.ua}></textarea>
          <InputLabel style={{ fontSize: "12px", marginTop: 4}}>{t('subscriptions.descDe')}</InputLabel>
          <textarea style={{ resize: "none", width: "100%" }} rows={3} onChange={event => setFieldValue('descriptionLocalized.de', event.target.value)} value={values.descriptionLocalized.de}></textarea>
          <InputLabel style={{ fontSize: "12px", marginTop: 4}}>{t('subscriptions.descEs')}</InputLabel>
          <textarea style={{ resize: "none", width: "100%" }} rows={3} onChange={event => setFieldValue('descriptionLocalized.es', event.target.value)} value={values.descriptionLocalized.es}></textarea>

          {isSuperAdmin ? (
            <Select
              style={{ width: '100%', marginTop: 20 }}
              label={t('subscriptions.city')}
              options={citiesOptions}
              onChange={value => setFieldValue('city', value)}
              value={values.city}
            />
          ) : null}
          <TextField
            fullWidth
            label={t('subscriptions.durationLabel')}
            margin='normal'
            type='number'
            name='duration'
            onChange={handleChange}
            value={values.duration}
          />

          <TextField
            fullWidth
            label={t('subscriptions.priceLabel')}
            margin='normal'
            type='number'
            name='price'
            onChange={handleChange}
            value={values.price}
          />

          <Select
            style={{ width: '100%', marginTop: 20 }}
            label={t('subscriptions.subscriptionType')}
            options={typeSelectorOptions}
            onChange={value => setFieldValue('type', value)}
            value={values.type}
            disabled={Boolean(selectedSubscription?._id)}
          />

          {values.type === ISubscriptionType.priceForContractActions ? (
            <SubscriptionModalPricesGroup
              label={t('subscriptions.contractPrices')}
              valueKey={'contractPrices'}
              values={values.contractPrices}
              onChange={(key, value) => {
                setFieldValue(`contractPrices.${key}`, value);
              }}
            />
          ) : null}

          {values.type === ISubscriptionType.freeContractActions ? (
            <>
              <SubscriptionModalPricesGroup
                label={t('subscriptions.freeActions')}
                valueKey={'freeActions'}
                values={values.freeActions}
                textFieldPlaceholder={t('subscriptions.quantity')}
                startTitle={t('subscriptions.startUnlocks')}
                onChange={(key, value) => {
                  setFieldValue(`freeActions.${key}`, value);
                }}
                only={Object.keys(defaultPricesFreeActionsData) as string[]}
              />

              <SubscriptionModalPricesGroup
                label={t('subscriptions.freeDuration')}
                textFieldPlaceholder={t('subscriptions.durationLabel')}
                valueKey={'freeDurationSeconds'}
                normalTitle={t('subscriptions.normalSeconds')}
                reservationTitle={t('subscriptions.reservationSeconds')}
                pauseTitle={t('subscriptions.pauseSeconds')}
                values={values.freeDurationSeconds}
                onChange={(key, value) => {
                  setFieldValue(`freeDurationSeconds.${key}`, value);
                }}
                only={Object.keys(defaultPricesDurationData) as string[]}
              />
            </>
          ) : null}

          {values.type ===
          ISubscriptionType.discountPercentForContractActions ? (
            <SubscriptionModalPricesGroup
              label={t('subscriptions.discountPercent')}
              valueKey={'contractPricesDiscountPercents'}
              values={values.contractPricesDiscountPercents}
              onChange={(key, value) => {
                setFieldValue(`contractPricesDiscountPercents.${key}`, value);
              }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button color='primary' onClick={() => handleSubmit()}>
            {t('subscriptions.save')}
          </Button>
        </DialogActions>
      </>
    </Dialog>
  );
};

export default SubscriptionModal;
